import { driver } from "driver.js";
import "driver.js/dist/driver.css";

// Same Calendly link as BookDemoTab.jsx.
export const BOOK_DEMO_URL = "https://calendly.com/eilon-stemmer/30min";

/**
 * waitForEl — polls until `selector` matches a DOM element or timeout elapses.
 * Resolves with the element on success, resolves with null on timeout (never throws).
 */
export function waitForEl(selector, timeout = 2500) {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      const found = document.querySelector(selector);
      if (found) {
        clearInterval(interval);
        resolve(found);
      } else if (Date.now() - start >= timeout) {
        clearInterval(interval);
        resolve(null); // degrade gracefully — caller skips the step
      }
    }, 80);
  });
}

export const FIRST_STEP_SELECTOR = '[data-tour="home"]';

/**
 * Tour steps. `to` navigates first; `prepare` runs before waiting for the
 * element; `wait` is how long to wait for it (a missing element skips the
 * step). No prices anywhere in this copy.
 */
export function buildDemoTourSteps(navigate) {
  const go = (path) => () => navigate && navigate(path);
  return [
    {
      id: "home",
      selector: '[data-tour="home"]',
      to: go("/home"),
      title: "Home",
      body: "What lands each day: capital calls, distributions and NAV updates, read from your portal documents.",
      side: "right",
    },
    {
      id: "ledger",
      selector: '[data-tour="dashboard"]',
      to: go("/dashboard"),
      title: "Ledger",
      body: "Every fund and investor: called, distributed and value, built automatically from the documents.",
      side: "right",
    },
    {
      id: "ledger-view-toggle",
      selector: '[data-tour="ledger-view-toggle"]',
      wait: 6000,
      title: "Positions or Lines",
      body: "Positions groups the book by fund and investor. Lines lists every single entry read from the documents.",
      side: "bottom",
    },
    {
      id: "ledger-filters",
      selector: '[data-tour="ledger-filters"]',
      wait: 3000,
      title: "Filters",
      body: "Narrow the book by portal, fund or search term. Totals update as you filter.",
      side: "bottom",
    },
    {
      id: "ledger-member",
      selector: '[data-tour="ledger-member"]',
      wait: 3000,
      // Funds start collapsed: open the first one so a member row exists.
      prepare: async () => {
        const toggle = await waitForEl('[data-tour="ledger-fund-toggle"]', 3000);
        if (toggle && !document.querySelector('[data-tour="ledger-member"]')) toggle.click();
      },
      title: "An investor",
      body: "Click an investor to see its KPIs and the Timeline of calls, distributions and value, each linked to its source document.",
      side: "top",
    },
    {
      id: "documents",
      selector: '[data-tour="documents"]',
      to: go("/documents"),
      title: "Documents",
      body: "Every capital call, distribution and statement pulled from your portals, classified and searchable.",
      side: "right",
    },
    {
      id: "lumos",
      selector: '[data-tour="lumos"]',
      title: "Lumos",
      body: "Ask plain-English questions about your portfolio and documents. Answers come from your own data.",
      side: "left",
    },
    {
      id: "groups",
      selector: '[data-tour="funds"]',
      to: go("/groups"),
      title: "My Groups",
      body: "Roll funds and investors into groups, such as a family or an entity, and see their combined position.",
      side: "right",
    },
    {
      id: "connections",
      selector: '[data-tour="connections"]',
      to: go("/settings"),
      title: "Settings: Connections",
      body: "Portals connect here. Login codes are forwarded to your own address, so there are no manual downloads.",
      side: "bottom",
    },
  ];
}

/**
 * startDemoTour — guided tour. Steps are shown one at a time; a step whose
 * element never appears is skipped rather than breaking the tour.
 *
 * @param {function} navigate           react-router navigate()
 * @param {function} [onCreateAccount]  handler for the final "Create your account" button
 */
export function startDemoTour(navigate, onCreateAccount) {
  const steps = buildDemoTourSteps(navigate);
  const total = steps.length + 1; // + closing card
  let drv = null;
  let ended = false;

  const finish = () => {
    ended = true;
    if (drv) drv.destroy();
  };

  // dir = +1 forward, -1 back; missing elements are skipped in that direction.
  async function runStep(i, dir) {
    if (ended) return;
    if (i >= steps.length) return showClosing();
    if (i < 0) return runStep(0, 1);
    const s = steps[i];
    if (s.to) s.to();
    if (s.prepare) await s.prepare();
    const el = await waitForEl(s.selector, s.wait || 2500);
    if (ended) return;
    if (!el) return runStep(i + dir, dir);
    drv.highlight({
      element: el,
      popover: {
        title: s.title,
        description: s.body,
        side: s.side,
        align: "start",
        showButtons: i === 0 ? ["next", "close"] : ["next", "previous", "close"],
        nextBtnText: "Next",
        prevBtnText: "Back",
        progressText: `${i + 1} of ${total}`,
        onCloseClick: () => finish(),
        onNextClick: () => runStep(i + 1, 1),
        onPrevClick: () => runStep(i - 1, -1),
      },
    });
  }

  function showClosing() {
    if (ended) return;
    drv.highlight({
      popover: {
        title: "Ready to see your own portfolio?",
        description: "Create an account and connect your portals, or book a live walkthrough.",
        showButtons: ["previous", "close"],
        prevBtnText: "Back",
        progressText: `${total} of ${total}`,
        onCloseClick: () => finish(),
        onPrevClick: () => runStep(steps.length - 1, -1),
        onPopoverRender: (popover) => {
          const row = document.createElement("div");
          row.style.cssText = "display:flex;gap:8px;margin-top:12px;flex-wrap:wrap";
          const create = document.createElement("button");
          create.type = "button";
          create.textContent = "Create your account";
          create.setAttribute("data-testid", "tour-create-account");
          create.style.cssText =
            "background:#C41E2A;color:#fff;border:0;border-radius:6px;padding:6px 12px;font-weight:600;cursor:pointer;text-shadow:none";
          create.onclick = () => {
            finish();
            if (onCreateAccount) onCreateAccount();
          };
          const book = document.createElement("a");
          book.href = BOOK_DEMO_URL;
          book.target = "_blank";
          book.rel = "noopener noreferrer";
          book.textContent = "Book a live demo";
          book.setAttribute("data-testid", "tour-book-demo");
          book.style.cssText =
            "border:1px solid #d1d5db;border-radius:6px;padding:6px 12px;color:#111;text-decoration:none;text-shadow:none";
          row.append(create, book);
          popover.footer.parentNode.insertBefore(row, popover.footer);
        },
      },
    });
  }

  drv = driver({
    animate: true,
    showProgress: true,
    allowClose: true,
    onDestroyed: () => {
      ended = true;
    },
  });
  runStep(0, 1);
}
